import { callUserApiPost } from '../user/callUserApi';

// 디바운스 및 중복 호출 방지를 위한 맵
const pendingRequests = new Map<string, Promise<any>>();

// 결제 요청 API 타입
export interface PaymentCreateRequest {
  orderId: string;
  amount: number;
}

export interface PaymentCreateResponse {
  success: boolean;
  orderId: string;
  amount: number;
  createdAt: string;
}

// 결제 승인 API 타입  
export interface PaymentApproveRequest {
  paymentKey: string;
  amount: number;
  orderId: string;
}

export interface PaymentApproveResponse {
  success: boolean;
  paymentKey: string;
  orderId: string;
  amount: number;
  approvedAt: string;
}

/**
 * 결제 요청 API - 토스 결제창 띄우기 전 호출 (중복 호출 방지)
 */
export const createPayment = async (data: PaymentCreateRequest): Promise<PaymentCreateResponse> => {
  const requestKey = `create_${data.orderId}_${data.amount}`;
  
  // 이미 진행 중인 동일한 요청이 있으면 해당 Promise 반환
  if (pendingRequests.has(requestKey)) {
    return pendingRequests.get(requestKey);
  }

  // 새로운 요청 생성 및 저장
  const requestPromise = (async () => {
    try {
      const response = await callUserApiPost<any>({
        title: '결제 요청',
        url: '/api/payment/create',
        body: data,
        withToken: true
      });

      if (response.data.statusCode === 200) {
        return {
          success: true,
          orderId: data.orderId,
          amount: data.amount,
          createdAt: new Date().toISOString()
        };
      } else {
        // 서버에서 반환하는 구체적인 에러 메시지 사용
        const errorMessage = response.data.error?.customMessage || 
                            response.data.message || 
                            '결제 요청에 실패했습니다.';
        throw new Error(errorMessage);
      }
    } finally {
      // 요청 완료 후 캐시에서 제거
      pendingRequests.delete(requestKey);
    }
  })();

  pendingRequests.set(requestKey, requestPromise);
  return requestPromise;
};

/**
 * 결제 승인 API - 토스 결제 완료 후 호출 (중복 호출 방지)
 */
export const approvePayment = async (data: PaymentApproveRequest): Promise<PaymentApproveResponse> => {
  const requestKey = `approve_${data.paymentKey}_${data.orderId}_${data.amount}`;
  
  // 이미 진행 중인 동일한 요청이 있으면 해당 Promise 반환
  if (pendingRequests.has(requestKey)) {
    return pendingRequests.get(requestKey);
  }

  // 새로운 요청 생성 및 저장
  const requestPromise = (async () => {
    try {
      const response = await callUserApiPost<any>({
        title: '결제 승인',
        url: '/api/payment/approve', 
        body: data,
        withToken: true
      });

      if (response.data.statusCode === 200) {
        return {
          success: true,
          paymentKey: data.paymentKey,
          orderId: data.orderId,
          amount: data.amount,
          approvedAt: new Date().toISOString()
        };
      } else {
        // 서버에서 반환하는 구체적인 에러 메시지 사용
        const errorMessage = response.data.error?.customMessage || 
                            response.data.message || 
                            '결제 승인에 실패했습니다.';
        throw new Error(errorMessage);
      }
    } finally {
      // 요청 완료 후 캐시에서 제거
      pendingRequests.delete(requestKey);
    }
  })();

  pendingRequests.set(requestKey, requestPromise);
  return requestPromise;
};